import { useMemo, useState, type FormEvent } from 'react'
import {
  AleloButton,
  AleloLayout,
  AleloPasswordInput,
} from '@/components/AleloLayout'
import styles from '@/components/AleloLayout.module.css'
import passwordStyles from './reset-password.module.css'
import { RangoAlert, RangoLink } from '@/components/rango'
import { executeSafely } from '@/utils/helpers'
import {
  classifySubmitError,
  useResetPasswordManager,
  type ResetPasswordError,
} from './hooks/useResetPasswordManager'
import { ResetPasswordOutcome } from './ResetPasswordOutcome'
import locales from './locales/pt-BR.json'

const fmt = (template: string, vars: Record<string, string | number>) =>
  template.replace(/\{(\w+)\}/g, (_, key) =>
    key in vars ? String(vars[key]) : `{${key}}`,
  )

function errorMessage(error: ResetPasswordError | null): string | undefined {
  if (!error) return undefined
  switch (error.code) {
    case 'WEAK_PASSWORD':
      return fmt(locales.weakPassword, { n: error.minLength })
    case 'MISMATCH':
      return locales.mismatch
    case 'POLICY':
      return locales.policy
    case 'EXPIRED_LINK':
      return locales.expiredLink
    default:
      return locales.failed
  }
}

/**
 * S8 — Nova senha (reset-password), aberta pelo link de recuperação.
 * Senha + confirmação com label visível, toggle, política e sucesso → login.
 */
export default function ResetPasswordScreen() {
  const { handleSubmit, goToLogin, minLength, username, texts, serverError } =
    useResetPasswordManager()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitError, setError] = useState<ResetPasswordError | null>(null)
  // A falha que o Auth0 devolveu no re-render vale até o próximo envio.
  const [submitted, setSubmitted] = useState(false)
  const error = submitError ?? (submitted ? null : (serverError ?? null))
  const [pending, setPending] = useState(false)
  const [empty, setEmpty] = useState<'password' | 'confirm' | null>(null)
  const [done, setDone] = useState(false)

  const message = useMemo(() => errorMessage(error), [error])
  const expired = error?.code === 'EXPIRED_LINK'
  const mismatch = error?.code === 'MISMATCH'
  const weak = error?.code === 'WEAK_PASSWORD'

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (pending) return
    setError(null)
    setSubmitted(true)
    const missing = !password ? 'password' : !confirm ? 'confirm' : null
    setEmpty(missing)
    if (missing) return
    setPending(true)
    try {
      await executeSafely(() => handleSubmit(password, confirm))
      setDone(true)
    } catch (err) {
      const detail = (err as Error & { detail?: ResetPasswordError }).detail
      setError(detail ?? classifySubmitError(err))
    } finally {
      setPending(false)
    }
  }

  if (done) {
    return (
      <ResetPasswordOutcome
        tone="positive"
        title={locales.successTitle}
        message={locales.success}
        action={{ label: locales.goToLogin, onClick: () => void goToLogin() }}
      />
    )
  }

  return (
    <AleloLayout>
      <h1 className={styles.title}>{texts?.title ?? locales.title}</h1>
      <p className={styles.subtitle}>
        {texts?.description ?? locales.description}
      </p>
      {username ? (
        <div className={styles.identifierRow}>
          <p className={styles.identifierLabel}>Conta</p>
          <p className={styles.identifierValue}>{username}</p>
        </div>
      ) : null}

      {expired ? (
        <RangoAlert
          tone="negative"
          title={locales.expiredTitle}
          className={styles.feedback}
        >
          {locales.expiredLink}
          <div className={passwordStyles.alertAction}>
            <RangoLink
              href="?screen=reset-password-request"
              className={passwordStyles.alertLink}
            >
              {locales.requestNewLink}
            </RangoLink>
          </div>
        </RangoAlert>
      ) : null}

      <form className={styles.stack} onSubmit={onSubmit} noValidate>
        <AleloPasswordInput
          id="password"
          required
          name="password"
          label={locales.passwordLabel}
          autoComplete="new-password"
          placeholder={
            texts?.passwordPlaceholder ?? locales.passwordPlaceholder
          }
          value={password}
          supportText={`${locales.policyTitle} ${fmt(locales.policyRule, { n: minLength })}`}
          onChange={(e) => {
            setPassword(e.target.value)
            setEmpty(null)
          }}
          error={
            empty === 'password'
              ? locales.emptyPassword
              : weak
                ? message
                : undefined
          }
          disabled={pending || expired}
          showLabel={locales.showPassword}
          hideLabel={locales.hidePassword}
        />
        <AleloPasswordInput
          id="confirm"
          required
          name="confirm"
          label={locales.confirmLabel}
          autoComplete="new-password"
          placeholder={locales.confirmPlaceholder}
          value={confirm}
          onChange={(e) => {
            setConfirm(e.target.value)
            setEmpty(null)
          }}
          error={
            empty === 'confirm'
              ? locales.emptyConfirm
              : mismatch
                ? message
                : undefined
          }
          disabled={pending || expired}
          showLabel={locales.showPassword}
          hideLabel={locales.hidePassword}
        />
        {error && !weak && !mismatch && !expired ? (
          <RangoAlert tone="negative">{message}</RangoAlert>
        ) : null}

        <AleloButton type="submit" disabled={pending || expired} loading={pending}>
          {pending ? locales.saving : (texts?.buttonText ?? locales.submit)}
        </AleloButton>
      </form>
    </AleloLayout>
  )
}
